import React from "react";
import toast from "react-hot-toast";

/**
 * Coerce a toast "message" into something React can actually render.
 *
 * Many call sites pass a raw API error body straight to `toast.error()` — most
 * notably an ASP.NET RFC 7807 problem-details object
 * `{ type, title, status, traceId }`. react-hot-toast renders the message as a
 * React child, and a plain object throws
 * "Objects are not valid as a React child", which white-screens the app.
 *
 * Strings, render functions, and React elements pass through untouched; any
 * other object (or Error) is reduced to a readable string.
 */
const toRenderableMessage = (msg) => {
  if (msg == null) return "";
  if (typeof msg === "string" || typeof msg === "function") return msg;
  if (React.isValidElement(msg)) return msg;
  if (msg instanceof Error) return msg.message || "Something went wrong";
  if (typeof msg === "object") {
    return (
      msg.message ||
      msg.detail ||
      msg.title ||
      msg.error ||
      "Something went wrong"
    );
  }
  return String(msg);
};

/**
 * Patch the shared `toast` singleton once. Every
 * `import toast from "react-hot-toast"` across the app resolves to this same
 * object, so wrapping its message-taking methods here hardens every toast call
 * app-wide — including future ones — without touching each call site.
 */
let patched = false;
export const hardenToast = () => {
  if (patched) return;
  patched = true;
  ["error", "success", "loading", "custom"].forEach((method) => {
    const original = toast[method];
    if (typeof original === "function") {
      toast[method] = (msg, options) => original(toRenderableMessage(msg), options);
    }
  });
};

hardenToast();

export default toast;
